import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useSpotifyClient } from '../spotify/useSpotifyClient.ts';
import { getDevices, getPlaybackState, pause, play, seek } from '../spotify/api.ts';
import { resolveTargetDevice } from '../spotify/deviceResolver.ts';
import type { SpotifyPlaybackState } from '../spotify/types.ts';
import { loadSettings, type Settings } from '../data/settings.ts';
import { listAllItems } from '../data/catalog.ts';
import { getProgress, progressFraction, saveProgress } from '../data/progress.ts';
import type { CatalogItem, ProgressEntry } from '../data/db.ts';

/** Wie oft der echte Wiedergabestatus bei Spotify abgefragt wird. */
const POLL_INTERVAL_MS = 5000;
/** Ruhiger Takt, solange nichts laeuft – schont Akku und Rate-Limit. */
const IDLE_POLL_INTERVAL_MS = 20_000;
/** Wie oft der Fortschritt spaetestens in die Datenbank geschrieben wird. */
const SAVE_INTERVAL_MS = 10_000;
/** Taktrate der lokalen Hochrechnung zwischen zwei Abfragen. */
const TICK_INTERVAL_MS = 1000;

export class NoDeviceError extends Error {
  constructor() {
    super('Kein steuerbares Spotify-Gerät verfügbar.');
    this.name = 'NoDeviceError';
  }
}

interface PlayerContextValue {
  playback: SpotifyPlaybackState | null;
  currentItem: CatalogItem | null;
  /** Hochgerechnete Position, damit die Anzeige zwischen Abfragen fluessig laeuft. */
  positionMs: number;
  /** 0-basierter Index des laufenden Kapitels. */
  chapterIndex: number;
  isPlaying: boolean;
  /** Kein Abspielgeraet erreichbar → „Hol einen Erwachsenen"-Bildschirm zeigen. */
  needsGrownUp: boolean;
  dismissGrownUp: () => void;
  startItem: (item: CatalogItem, fromStart?: boolean) => Promise<void>;
  togglePlayPause: () => Promise<void>;
  skip: (deltaMs: number) => Promise<void>;
  /** Ein Kapitel vor oder zurueck. */
  skipChapter: (delta: number) => Promise<void>;
  refreshCatalog: () => Promise<void>;
}

const PlayerContext = createContext<PlayerContextValue | null>(null);

export function PlayerProvider({ children }: { children: ReactNode }) {
  const client = useSpotifyClient();

  const [playback, setPlayback] = useState<SpotifyPlaybackState | null>(null);
  const [currentItem, setCurrentItem] = useState<CatalogItem | null>(null);
  const [positionMs, setPositionMs] = useState(0);
  const [needsGrownUp, setNeedsGrownUp] = useState(false);

  const settingsRef = useRef<Settings>(loadSettings());
  const itemsRef = useRef<CatalogItem[]>([]);
  const currentItemRef = useRef<CatalogItem | null>(null);
  const playbackRef = useRef<SpotifyPlaybackState | null>(null);
  const positionRef = useRef(0);
  const lastPollAtRef = useRef(0);
  const lastSavedAtRef = useRef(0);

  const refreshCatalog = useCallback(async () => {
    itemsRef.current = await listAllItems();
    settingsRef.current = loadSettings();
  }, []);

  useEffect(() => {
    void refreshCatalog();
  }, [refreshCatalog]);

  /** Schreibt den aktuellen Stand in die Datenbank. */
  const persistProgress = useCallback(async () => {
    const item = currentItemRef.current;
    const state = playbackRef.current;
    if (!item || !state?.item) return;

    const trackNumber = 'track_number' in state.item ? state.item.track_number : 1;
    const trackIndex = Math.max(0, trackNumber - 1);
    const fraction = progressFraction(
      trackIndex,
      positionRef.current,
      state.item.duration_ms,
      item.trackCount,
    );

    const entry: ProgressEntry = {
      itemId: item.id,
      state: fraction >= settingsRef.current.finishedThreshold ? 'finished' : 'inProgress',
      trackIndex,
      positionMs: positionRef.current,
      trackCount: item.trackCount,
      lastPlayedAt: Date.now(),
    };

    await saveProgress(entry);
    lastSavedAtRef.current = Date.now();
  }, []);

  /** Holt den echten Stand bei Spotify und ordnet ihn einer Katalogfolge zu. */
  const poll = useCallback(async () => {
    try {
      const state = await getPlaybackState(client);
      playbackRef.current = state;
      setPlayback(state);
      lastPollAtRef.current = Date.now();

      if (state?.progress_ms != null) {
        positionRef.current = state.progress_ms;
        setPositionMs(state.progress_ms);
      }

      const contextUri = state?.context?.uri;
      const matched = contextUri
        ? (itemsRef.current.find((item) => item.uri === contextUri) ?? null)
        : null;
      currentItemRef.current = matched;
      setCurrentItem(matched);

      if (matched && state?.is_playing && Date.now() - lastSavedAtRef.current > SAVE_INTERVAL_MS) {
        await persistProgress();
      }
    } catch {
      // Abfragefehler bewusst still – der Nutzer sieht Fehler nur bei eigenen Aktionen.
    }
  }, [client, persistProgress]);

  // Regelmaessig abfragen, aber nur wenn die Seite sichtbar ist.
  // Laeuft nichts, reicht ein ruhigerer Takt.
  useEffect(() => {
    let timer: number | undefined;

    const schedule = () => {
      const interval = playbackRef.current?.is_playing
        ? POLL_INTERVAL_MS
        : IDLE_POLL_INTERVAL_MS;
      timer = window.setTimeout(async () => {
        if (document.visibilityState === 'visible') await poll();
        schedule();
      }, interval);
    };

    void poll();
    schedule();

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [poll]);

  // Zwischen den Abfragen lokal hochrechnen, damit die Anzeige nicht ruckelt.
  useEffect(() => {
    const timer = window.setInterval(() => {
      const state = playbackRef.current;
      if (!state?.is_playing) return;
      const elapsed = Date.now() - lastPollAtRef.current;
      const base = state.progress_ms ?? 0;
      const duration = state.item?.duration_ms ?? Number.POSITIVE_INFINITY;
      const next = Math.min(base + elapsed, duration);
      positionRef.current = next;
      setPositionMs(next);
    }, TICK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, []);

  /**
   * Sofort speichern, wenn die App in den Hintergrund geht oder geschlossen
   * wird. Genau das verhindert den Frust, dass eine Folge wieder von vorne
   * beginnt, nachdem das Kind versehentlich rausgegangen ist.
   */
  useEffect(() => {
    const flush = () => {
      void persistProgress();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
      else void poll();
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
    };
  }, [persistProgress, poll]);

  const resolveDeviceId = useCallback(async (): Promise<string> => {
    const devices = await getDevices(client);
    const settings = settingsRef.current;
    const resolved = resolveTargetDevice(devices, settings.primaryDevice, settings.fallbackDevice);
    if (!resolved?.device.id) {
      setNeedsGrownUp(true);
      throw new NoDeviceError();
    }
    setNeedsGrownUp(false);
    return resolved.device.id;
  }, [client]);

  const startItem = useCallback(
    async (item: CatalogItem, fromStart = false) => {
      const deviceId = await resolveDeviceId();
      const entry = fromStart ? undefined : await getProgress(item.id);

      // Eine bereits durchgehoerte Folge beginnt wieder von vorn.
      const resume = entry && entry.state === 'inProgress' ? entry : undefined;
      const trackIndex = resume?.trackIndex ?? 0;
      const positionMs = resume
        ? Math.max(0, resume.positionMs - settingsRef.current.resumeRewindMs)
        : 0;

      currentItemRef.current = item;
      setCurrentItem(item);

      await play(client, {
        deviceId,
        contextUri: item.uri,
        offsetPosition: trackIndex,
        positionMs,
      });

      // Kurz warten, damit Spotify den neuen Stand gesetzt hat.
      window.setTimeout(() => void poll(), 800);
    },
    [client, poll, resolveDeviceId],
  );

  const togglePlayPause = useCallback(async () => {
    const deviceId = await resolveDeviceId();
    if (playbackRef.current?.is_playing) {
      await pause(client, deviceId);
      await persistProgress();
    } else {
      await play(client, { deviceId });
    }
    window.setTimeout(() => void poll(), 500);
  }, [client, persistProgress, poll, resolveDeviceId]);

  const skip = useCallback(
    async (deltaMs: number) => {
      const deviceId = await resolveDeviceId();
      const target = Math.max(0, positionRef.current + deltaMs);
      await seek(client, target, deviceId);
      window.setTimeout(() => void poll(), 500);
    },
    [client, poll, resolveDeviceId],
  );

  /**
   * Springt gezielt ein Kapitel vor oder zurueck.
   *
   * Bewusst ueber `play` mit festem Offset statt ueber Spotifys
   * next/previous: `previous` springt je nach Position mal an den
   * Kapitelanfang und mal ins vorige Kapitel. Fuer ein Kind muss dieselbe
   * Taste aber immer dasselbe tun.
   */
  const skipChapter = useCallback(
    async (delta: number) => {
      const item = currentItemRef.current;
      const state = playbackRef.current;
      if (!item) return;

      const currentIndex =
        state?.item && 'track_number' in state.item ? state.item.track_number - 1 : 0;
      const target = Math.min(item.trackCount - 1, Math.max(0, currentIndex + delta));
      if (target === currentIndex && delta !== 0) return;

      const deviceId = await resolveDeviceId();
      await play(client, {
        deviceId,
        contextUri: item.uri,
        offsetPosition: target,
        positionMs: 0,
      });
      window.setTimeout(() => void poll(), 800);
    },
    [client, poll, resolveDeviceId],
  );

  const chapterIndex =
    playback?.item && 'track_number' in playback.item ? playback.item.track_number - 1 : 0;

  const value = useMemo<PlayerContextValue>(
    () => ({
      playback,
      currentItem,
      positionMs,
      chapterIndex,
      isPlaying: playback?.is_playing ?? false,
      needsGrownUp,
      dismissGrownUp: () => setNeedsGrownUp(false),
      startItem,
      togglePlayPause,
      skip,
      skipChapter,
      refreshCatalog,
    }),
    [
      playback,
      currentItem,
      positionMs,
      chapterIndex,
      needsGrownUp,
      startItem,
      togglePlayPause,
      skip,
      skipChapter,
      refreshCatalog,
    ],
  );

  return <PlayerContext value={value}>{children}</PlayerContext>;
}

export function usePlayer(): PlayerContextValue {
  const ctx = useContext(PlayerContext);
  if (!ctx) throw new Error('usePlayer muss innerhalb von <PlayerProvider> genutzt werden.');
  return ctx;
}
